import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";
import { ADMIN_EXTRA as T } from "@/constants/testIds";
import { AdminBarcodeSuggestions } from "@/components/AdminBarcodeSuggestions";

const DownloadButton = ({ fmt, label }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/feeds/google-products.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `pettokshop-google-products.${fmt}` });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <button data-testid={T.merchantDownload(fmt)} onClick={download} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1C1917] text-white text-sm font-medium hover:bg-[#E06D44] disabled:opacity-60 transition-colors">
      <Download size={15} /> {busy ? "Preparing…" : label}
    </button>
  );
};

const Field = ({ k, label, value }) => {
  const copy = () => navigator.clipboard.writeText(String(value)).then(() => toast.success(`${label} copied`), () => toast.error("Couldn't copy, select the text instead"));
  return (
    <div className="flex items-center gap-3 py-2 border-b border-[#F0EBE1] last:border-0 text-sm">
      <span className="w-44 shrink-0 text-stone-500">{label}</span>
      <span data-testid={T.merchantValue(k)} className="flex-1 min-w-0 break-words font-medium">{value}</span>
      <button data-testid={T.merchantCopy(k)} aria-label={`Copy ${label}`} onClick={copy} className="p-2 rounded-full hover:bg-[#F0EBE1] transition-colors"><Copy size={14} /></button>
    </div>
  );
};

const Card = ({ step, title, children }) => (
  <div className="bg-white border border-[#F0EBE1] rounded-2xl p-5">
    <p className="text-xs font-semibold text-[#E06D44]">Step {step}</p>
    <h3 className="font-heading font-bold text-lg">{title}</h3>
    <div className="mt-2">{children}</div>
  </div>
);

export const AdminMerchantSetup = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/merchant-setup").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="mt-6 text-stone-500">Loading Google setup…</p>;
  const s = d.shipping;
  const r = d.returns;
  return (
    <section data-testid={T.merchant} className="mt-6 grid lg:grid-cols-2 gap-4">
      <Card step={1} title="Add your product feed">
        <p className="text-sm text-stone-600">In Merchant Center go to Products → Add products → Add from a file → Enter a link to your file, paste this link and choose a daily schedule. {d.products_in_feed} products are in it now.</p>
        <Field k="feed" label="Feed link" value={d.feed_url} />
        <div className="pt-2 flex flex-wrap gap-2">
          <DownloadButton fmt="csv" label="Download for Google Sheets (.csv)" />
          <DownloadButton fmt="xml" label="Download XML file" />
        </div>
        <p className="text-xs text-stone-500">Files always contain your current active products. To use Google Sheets: open sheets.new → File → Import → Upload the .csv, then in Merchant Center add products from Google Sheets.</p>
      </Card>
      {d.held_back.length > 0 && (
        <div data-testid="merchant-held-back" className="lg:col-span-2 rounded-2xl p-5 bg-amber-50 border border-amber-200 text-sm">
          <h3 className="font-heading font-bold text-lg">{d.held_back.length} products held out of Google until they get their own photo</h3>
          <p className="mt-1 text-stone-600">These share a stand-in photo with another product, which Google rejects as "image doesn't match product". Upload a real photo in the Products tab and each one joins the feed automatically on Google's next daily fetch.</p>
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {d.held_back.map((p) => <li key={p.slug} data-testid={`merchant-held-${p.slug}`} className="px-3 py-1 rounded-full bg-white border border-amber-200">{p.name}</li>)}
          </ul>
        </div>
      )}
      {d.barcode_suggestions.length > 0 && <AdminBarcodeSuggestions key={d.barcode_suggestions.length} suggestions={d.barcode_suggestions} onSaved={load} />}
      {d.needs_barcode.length > 0 && (
        <div data-testid="merchant-needs-barcode" className="lg:col-span-2 rounded-2xl p-5 bg-white border border-[#F0EBE1] text-sm">
          <h3 className="font-heading font-bold text-lg">{d.needs_barcode.length} name-brand products need their barcode</h3>
          <p className="mt-1 text-stone-600">Google expects the barcode number (GTIN) for brands like Ruff Dawg, Nylabone and PetSafe, and may mark them "missing GTIN" or limit their reach without it. Find the 12 or 13 digits under the barcode on the box, then add them in Products → Details → Barcode / GTIN.</p>
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {d.needs_barcode.map((p) => <li key={p.slug} data-testid={`merchant-barcode-${p.slug}`} className="px-3 py-1 rounded-full bg-[#FDFBF7] border border-[#E7E0D3]">{p.name}</li>)}
          </ul>
        </div>
      )}
      <Card step={2} title="Shipping (Settings → Shipping and returns → Shipping)">
        <Field k="ship-country" label="Country" value={`${s.country} (${s.currency})`} />
        <Field k="ship-rate" label="Shipping rate" value={`${money(s.rate)} flat, free on orders ${money(s.free_over)}+`} />
        <Field k="ship-handling" label="Handling time" value={`${s.handling_days[0]}–${s.handling_days[1]} business days`} />
        <Field k="ship-transit" label="Transit time" value={`${s.transit_days[0]}–${s.transit_days[1]} business days`} />
        <Field k="ship-url" label="Shipping policy page" value={d.policy_urls.shipping} />
      </Card>
      <Card step={3} title="Returns (Settings → Shipping and returns → Returns)">
        <Field k="ret-country" label="Country" value={r.country} />
        <Field k="ret-window" label="Return window" value={`${r.window_days} days`} />
        <Field k="ret-cost" label="Return shipping" value={r.cost} />
        <Field k="ret-fee" label="Restocking fee" value={r.restocking_fee} />
        <Field k="ret-method" label="Return method" value={r.method} />
        <Field k="ret-refund" label="Refund" value={r.refund} />
        <Field k="ret-condition" label="Item condition" value={r.condition} />
        <Field k="ret-url" label="Return policy page" value={r.policy_url} />
      </Card>
      <Card step={4} title="Business details">
        <p className="text-sm text-stone-600">Under Settings → Business info, add your contact page so Google can verify you're reachable. Then wait for the review (usually 3–5 business days).</p>
        <Field k="contact-url" label="Contact page" value={d.policy_urls.contact} />
      </Card>
    </section>
  );
};
